import { ArrowRight, Zap, Globe, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import InstallAppModal from './InstallAppModal';
import { useLanguage } from '@/i18n/LanguageContext';
import logo from '@/assets/igniteaitool-logo.jpg';

interface HeroSectionProps {
  onGetStarted: () => void;
  remainingRequests?: number;
  isTrialUser?: boolean;
}

const HeroSection = ({ onGetStarted, remainingRequests, isTrialUser }: HeroSectionProps) => {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden py-6 sm:py-8 px-4">
      <div className="absolute inset-0 bg-[var(--hero-gradient)] opacity-30" />
      
      <div className="container mx-auto max-w-4xl text-center relative z-10">
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 animate-in fade-in slide-in-from-bottom-4 duration-1000">
          <img
            src={logo}
            alt="IgniteAiTool logo"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full ring-2 ring-primary/40 shadow-md"
          />
          <p className="text-base sm:text-lg md:text-xl font-bold text-primary">
            {t('hero.badge')}
          </p>
        </div>
        
        <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-3 sm:mb-4 leading-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-in fade-in slide-in-from-bottom-4 duration-1000">
          {t('hero.title')}
        </h1>
        
        <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-3 sm:mb-4 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">
          {t('hero.subtitle')}
        </p>

        {/* Value proposition bullets */}
        <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-2 sm:gap-4 mb-3 sm:mb-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200">
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
            <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
            {t('hero.valueBullet1')}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
            <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
            {t('hero.valueBullet2')}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
            {t('hero.valueBullet3')}
          </span>
        </div>

        {/* Urgency badge */}
        <div className="mb-3 sm:mb-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-250">
          {isTrialUser && remainingRequests !== undefined ? (
            <span className="inline-block rounded-full bg-destructive/10 text-destructive px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold">
              {t('hero.urgencyDynamic').replace('{count}', String(remainingRequests))}
            </span>
          ) : (
            <span className="inline-block rounded-full bg-destructive/10 text-destructive px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold">
              {t('hero.urgency')}
            </span>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              <div className="absolute -inset-1 rounded-lg bg-primary/20 animate-pulse" />
              <Button 
                size="lg" 
                onClick={onGetStarted}
                className="relative group text-sm sm:text-base px-6 sm:px-10 py-5 sm:py-6 shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
              >
                {t('hero.ctaButton')}
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
              <span>{t('hero.noCreditCard')}</span>
              <span className="text-muted-foreground/50">·</span>
              <span>{t('hero.trustSecure')}</span>
            </div>
          </div>
          
          <InstallAppModal />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
